import { isNative } from './device.js';

/**
 * Phone roll from the accelerometer, for every capture that measures angles:
 * a phone tilted 2° in the picture plane adds 2° to each angle the app reads.
 *
 * Two readings: roll (sideways, in the picture plane) and pitch (leaning back
 * or forward). Roll is corrected — landmarks are rotated level before angles
 * are read — so a phone propped against something does not need to be
 * plumb. Pitch cannot be corrected without the lens geometry; it is recorded.
 *
 * iOS only delivers motion after DeviceMotionEvent.requestPermission(), called
 * inside a tap. In the native app Capacitor grants it without a prompt, so the
 * first tap anywhere asks (`installMotionPermission`); in a browser the capture
 * screens ask from their own buttons.
 */
type MotionPermission = {
  requestPermission?: () => Promise<'granted' | 'denied'>;
};

/** Smoothed "up" in device axes (x right, y up the screen, z out of it), y ≥ 0. */
let up: { x: number; y: number; z: number } | null = null;
let listening = false;
let permission: 'unknown' | 'granted' | 'denied' = 'unknown';

function onMotion(event: DeviceMotionEvent): void {
  const g = event.accelerationIncludingGravity;
  if (!g || g.x === null || g.y === null) return;
  // iOS reports the whole vector with the opposite sign to the spec (and to
  // Android). The phone is upright in portrait, so "up" has y > 0: normalising
  // on that removes the platform difference without a platform check.
  const flip = g.y < 0 ? -1 : 1;
  const next = { x: g.x * flip, y: g.y * flip, z: (g.z ?? 0) * flip };
  if (next.x === 0 && next.y === 0) return;
  // Light smoothing: hand-held jitter should not flicker the checklist.
  up = up === null ? next : { x: up.x * 0.8 + next.x * 0.2, y: up.y * 0.8 + next.y * 0.2, z: up.z * 0.8 + next.z * 0.2 };
}

function supported(): boolean {
  return typeof window !== 'undefined' && typeof DeviceMotionEvent !== 'undefined';
}

/** Ask for motion access if this platform needs it. Must run inside a tap on iOS. */
export async function requestMotionPermission(): Promise<boolean> {
  if (!supported()) return false;
  if (permission !== 'unknown') return permission === 'granted';
  const request = (DeviceMotionEvent as unknown as MotionPermission).requestPermission;
  if (typeof request !== 'function') {
    permission = 'granted';
    return true;
  }
  try {
    permission = (await request()) === 'granted' ? 'granted' : 'denied';
  } catch {
    // Not inside a gesture yet: leave it unknown so the next tap can ask.
    return false;
  }
  return permission === 'granted';
}

/** Native app: unlock motion on the first tap anywhere, silently. */
export function installMotionPermission(): void {
  if (!isNative() || !supported()) return;
  const once = (): void => {
    void requestMotionPermission().then((ok) => {
      if (!ok && permission === 'unknown') document.addEventListener('pointerup', once, { once: true });
    });
  };
  document.addEventListener('pointerup', once, { once: true });
}

/** Start reading tilt (asks for permission when possible). False where unavailable. */
export async function startTiltWatch(): Promise<boolean> {
  if (!(await requestMotionPermission())) return false;
  if (!listening) {
    window.addEventListener('devicemotion', onMotion);
    listening = true;
  }
  return true;
}

export function stopTiltWatch(): void {
  if (typeof window !== 'undefined') window.removeEventListener('devicemotion', onMotion);
  listening = false;
  up = null;
}

/** Sideways roll in the screen plane, degrees, as a magnitude; null before the first reading. */
export function currentTilt(): number | null {
  return up === null ? null : (Math.atan2(Math.abs(up.x), up.y) * 180) / Math.PI;
}

/**
 * Roll as seen in the camera picture, signed: the angle from the picture's
 * "up" to true up, positive toward the picture's +x. The rear camera sees what
 * the screen shows; the front camera's (unmirrored) picture has x reversed.
 * Used to level landmarks before any angle is read.
 */
export function currentImageRoll(facing: 'user' | 'environment'): number | null {
  if (up === null) return null;
  const x = facing === 'user' ? -up.x : up.x;
  return (Math.atan2(x, up.y) * 180) / Math.PI;
}

/** How far the phone leans back or forward (out of the screen plane), degrees. */
export function currentPitch(): number | null {
  if (up === null) return null;
  return (Math.atan2(Math.abs(up.z), Math.hypot(up.x, up.y)) * 180) / Math.PI;
}

/** Median of a list of tilt readings, or null. */
export function medianTilt(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  return Math.round(sorted[Math.floor(sorted.length / 2)]! * 10) / 10;
}
